import type { Dayjs } from 'dayjs'
import type { DisciplineDetails } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useListsStore } from '@shared/model'
import { useAdminRoutes } from '@shared/lib/routes'

import { useDisciplineStore } from './store'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineDetailsApi } from '../api/details.hook'
import { useDisciplineShowApi } from '../api/show.hook'
import { getCustomerNow } from './discipline-schedule'

interface DisciplineScheduleContextReturn {
  refDetails: ComputedRef<DisciplineDetails | null>
  refDisciplineId: ComputedRef<string>
  refKidName: ComputedRef<string>
  refCustomerTimezone: ComputedRef<string>
  /** The discipline subject as the admin lists name it, e.g. "Math". */
  refDisciplineLabel: ComputedRef<string>
  /** The subject as the customer sees it. */
  refCustomerDisciplineLabel: ComputedRef<string>
  refTutorName: ComputedRef<string>
  refTutorUrl: ComputedRef<string | null>
  customerNow: () => Dayjs
  formatCustomerNow: () => string
  close: () => void
  back: () => void
  refreshDiscipline: () => Promise<void>
}

/** What every weekly-schedule modal needs: the discipline, the customer's clock and the wizard's navigation. */
export const useDisciplineScheduleContext = (): DisciplineScheduleContextReturn => {
  const route = useRoute()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const store = useDisciplineScheduleStore()

  const refDetails = computed<DisciplineDetails | null>(() => disciplineStore.getDetails)
  const refDisciplineId = computed<string>(() => route.params.id as string)
  const refKidName = computed<string>(() => refDetails.value?.kid.name ?? '')
  const refCustomerTimezone = computed<string>(() => refDetails.value?.customer?.timezone ?? '')

  const refDisciplineLabel = computed<string>(() => {
    const code = refDetails.value?.code ?? ''
    return listsStore.getLists?.discipline.discipline_key[code] ?? code
  })

  const refCustomerDisciplineLabel = computed<string>(() => {
    const code = refDetails.value?.code ?? ''
    return listsStore.getLists?.discipline.code_for_customer[code] ?? code
  })

  const refTutorName = computed<string>(() => {
    const details = refDetails.value
    const tutor = details?.tutors.find(item => item.id === details.tutor_id)

    return tutor ? `${tutor.first_name} ${tutor.last_name}` : '-'
  })

  const refTutorUrl = computed<string | null>(() => {
    const tutorId = refDetails.value?.tutor_id
    return tutorId ? useAdminRoutes('admin.tutor.show', { tutor_id: tutorId }) : null
  })

  const customerNow = (): Dayjs => getCustomerNow(refCustomerTimezone.value)

  const formatCustomerNow = (): string => customerNow().format(DateFormat.KanbanDate)

  const close = (): void => {
    if (store.isSaving) return
    store.closeFlow()
  }

  const back = (): void => {
    if (store.isSaving) return
    store.previousStep()
  }

  /** The modal keeps its own loader over the page, so the reread stays silent. */
  const refreshDiscipline = async (): Promise<void> => {
    const disciplineId = refDisciplineId.value

    await Promise.all([
      useDisciplineDetailsApi({ disciplineId, silent: true }),
      useDisciplineShowApi({ disciplineId, silent: true }),
    ])
  }

  return {
    refDetails,
    refDisciplineId,
    refKidName,
    refCustomerTimezone,
    refDisciplineLabel,
    refCustomerDisciplineLabel,
    refTutorName,
    refTutorUrl,
    customerNow,
    formatCustomerNow,
    close,
    back,
    refreshDiscipline,
  }
}
